import { useState, useCallback } from 'react';

/**
 * Hook para gerenciar a navegação por gestos touch na timeline
 * 
 * @param {Object} options - Opções de configuração
 * @param {boolean} options.isMobile - Se estamos em um dispositivo móvel
 * @param {Array} options.resources - Lista de recursos disponíveis
 * @param {number} options.visibleColumns - Número de colunas visíveis
 * @param {number} options.resourceStartIndex - Índice inicial de recursos visíveis
 * @param {Function} options.setResourceStartIndex - Função para atualizar o índice inicial
 * @returns {Object} - Estados e handlers para gerenciar gestos touch
 */
const useTimelineTouchNavigation = ({
  isMobile,
  resources,
  visibleColumns,
  resourceStartIndex,
  setResourceStartIndex
}) => {
  // Estado para controlar touch/gestos
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Configuração de sensibilidade do swipe
  const minSwipeDistance = 50;

  // Funções para detectar gestos de swipe
  const onTouchStart = (e) => {
    // Verificar se o toque está na toolbar ou em seus elementos filhos
    if (e.target.closest('.rbc-toolbar') || 
        e.target.closest('button') || 
        e.target.closest('.toolbar-buttons') || 
        e.target.closest('.rbc-btn-group')) {
      // Se o toque foi na toolbar ou em qualquer botão, retornar sem processar
      return;
    }
    
    // Se o toque for em um evento do calendário, não processar como swipe
    if (e.target.closest('.rbc-event') || e.target.closest('.rbc-event-content') || e.target.closest('.rbc-event-content-wrapper')) {
      return;
    }
    
    // Armazenar a posição inicial apenas se houver mais de um profissional/recurso
    if (resources.length > visibleColumns) {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
    }
  };

  const onTouchMove = useCallback((e) => {
    // Se não estamos no mobile ou não temos touchStart, retorna
    if (!isMobile || !touchStart) return;
    
    // Se o toque foi na toolbar ou em um botão, ignorar
    if (e.target.closest('.rbc-toolbar') || e.target.closest('button')) return;
    
    // Se estamos sobre um evento, não processar swipe
    if (e.target.closest('.rbc-event')) return;
    
    // Processar toque horizontal para navegação entre profissionais
    setTouchEnd(e.targetTouches[0].clientX);
  }, [isMobile, touchStart]);

  const onTouchEnd = (e) => {
    // Se o toque terminou em um evento do calendário, não processar como swipe
    if (e.target.closest('.rbc-event') || e.target.closest('.rbc-event-content') || e.target.closest('.rbc-event-content-wrapper')) {
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }
    
    // Processar o swipe apenas se tivermos pontos de início e fim válidos
    if (!touchStart || !touchEnd || resources.length <= visibleColumns) {
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      navigateToNextResourceSet();
    }
    if (isRightSwipe) {
      navigateToPrevResourceSet();
    }
    
    // Limpar os pontos de toque
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Funções para navegar entre conjuntos de recursos
  const navigateToNextResourceSet = useCallback(() => {
    if (resourceStartIndex + visibleColumns < resources.length) {
      setResourceStartIndex(prevIndex => Math.min(prevIndex + 1, resources.length - visibleColumns));
    }
  }, [resourceStartIndex, visibleColumns, resources.length, setResourceStartIndex]);

  const navigateToPrevResourceSet = useCallback(() => {
    if (resourceStartIndex > 0) {
      setResourceStartIndex(prevIndex => Math.max(prevIndex - 1, 0));
    }
  }, [resourceStartIndex, setResourceStartIndex]);

  return {
    touchStart,
    touchEnd,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    navigateToNextResourceSet,
    navigateToPrevResourceSet
  };
};

export default useTimelineTouchNavigation; 